import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignNPC,
} from "@dndModels";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CampaignNPCInput from "./CampaignNPCInput";
import DandDAPI from "@dndUtilities/system_api";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";

interface Props {
  campaign: Campaign;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignNPCsInput({
  campaign,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    if (campaign.npcs.length === 0) {
      campaign.npcs = await api.getCampaignObjects<CampaignNPC>({ data_type: "campaign_npc", campaign });
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderNPCs = () => {
    return (
      <FlexBox>
        <Grid container spacing={0} direction="row">
          { campaign.npcs.map(npc => {
            return (
              <Grid size={{ xs:12, sm: 6, md: 4, lg: 3 }} key={npc._id}>
                <CampaignNPCInput
                  campaign={campaign}
                  npc={npc} 
                  onDelete={ async () => {
                    setProcessing(true);
                    if (npc.char_sheet) {
                      await api.deleteObject("character", npc.char_sheet);
                    }
                    await api.deleteObject("campaign_npc", npc);
                    campaign.npcs = campaign.npcs.filter(o => o._id !== npc._id);
                    setProcessing(false);
                  }} 
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    )
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      <FlexBox className="padded">
        <AddButton label="Add NPC" 
          disabled={processing}
          onClick={ async () => {
            const new_npc = new CampaignNPC();
            new_npc.campaign_id = campaign._id;
            setProcessing(true);
            await api.upsertObject("campaign_npc", new_npc);
            campaign.npcs.push(new_npc);
            setProcessing(false);
          }}
        />
      </FlexBox>
      { renderNPCs() }
    </FlexBox>
  );
}
